import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useCrmTagsQuery, useDestinationsQuery, useReviewItemsQuery, type CrmTagVocabulary, type ReviewRow } from './api.js';
import { rowTagLabels } from './crmTagSelection.js';
import { CaptureDetail } from './CaptureReview.js';
import { ConversationDetail } from './ConversationReview.js';
import { BatchDetail } from './BatchDetail.js';
import { BusinessCard } from './BusinessCard.js';
import { ArchivedMessages, useHasHistory } from './EmailIntelligence.js';
import { ReviewDrawer } from './ReviewDrawer.js';
import {
  SOURCE_LABEL, VIEWS, VIEW_LABEL, batchLine, emptyCopy, rowReason, rowStatus,
  type ReviewStateKey,
} from './reviewModel.js';
import { REVIEW_SOURCES, type ReviewFilters, type ReviewSourceFilter } from './navigation.js';
import { Alert, EmptyState, Spinner, formatWhen } from './ui.js';


/**
 * Review: one place for every decision, and for what came of it.
 *
 * Capture is how information enters; Review is where a person decides what reaches the
 * CRM and later checks what did. The VIEW is the primary question — "what is waiting for
 * me?" — in four words: Needs review, Sent, Not sent, All (reviewModel.ts says which
 * states each holds). SOURCE — email, business card, import — is only a filter over it,
 * because where a contact came from changes how much to trust it, not whether it needs
 * deciding.
 *
 * The list owns the page. An item opens in the one Review drawer, over the list, which
 * does not move; closing it returns focus to the row. Every decision is still made in the
 * source's own detail — the capture, the card form, the import — which re-checks every
 * gate it always did.
 */

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';

/** Tone per state, on the status text. Never the only signal: the text says the state. */
const STATE_TONE: Record<ReviewStateKey, string> = {
  needs_review: 'text-amber-900',
  ready: 'text-sky-900',
  waiting: 'text-slate-700',
  sent: 'text-emerald-800',
  failed: 'text-red-800',
  rejected: 'text-slate-600',
  processing: 'text-slate-600',
};
const STATE_DOT: Record<ReviewStateKey, string> = {
  needs_review: 'bg-amber-500',
  ready: 'bg-sky-500',
  waiting: 'bg-slate-400',
  sent: 'bg-emerald-500',
  failed: 'bg-red-500',
  rejected: 'bg-slate-300',
  processing: 'bg-slate-300',
};

const SOURCE_OPTIONS: Record<ReviewSourceFilter, string> = {
  all: 'All',
  email: 'Email',
  business_card: 'Business Card',
  import: 'Import',
  web_page: 'Web page',
};

/** The one line that names a row. A batch is its label, or how many contacts it holds. */
function identityOf(row: ReviewRow): string {
  // A conversation names each person once: "Jane Smith +2" is three people, not three emails.
  // One stable name for the list and the drawer alike, from the server; an older server's names otherwise.
  if (row.kind === 'email') return row.title ?? (row.names.length > 1 ? `${row.names[0]} +${row.names.length - 1}` : (row.names[0] ?? row.from));
  if (row.kind === 'card') return row.name ?? 'Business card';
  return row.label ?? `${row.total} contact${row.total === 1 ? '' : 's'}`;
}

function companyOf(row: ReviewRow): string | null {
  if (row.kind === 'batch') {
    return row.batchSource === 'csv_import' ? 'File import'
      : row.batchSource === 'contact_list_image' ? 'Photographed list' : 'Several business cards';
  }
  return row.company;
}

/** What a drawer is titled: the person or the company, or the source when there is neither. */
function drawerTitle(row: ReviewRow): { title: string; subtitle: string | null } {
  const company = companyOf(row);
  if (row.kind === 'batch') return { title: identityOf(row), subtitle: null };
  if (row.kind === 'email') {
    // The same name the list shows, so opening a row never seems to open someone else.
    const name = row.title ?? row.names[0] ?? null;
    return name ? { title: name, subtitle: company } : { title: company ?? row.from ?? 'Email', subtitle: null };
  }
  return row.name ? { title: row.name, subtitle: company } : { title: company ?? 'Business card', subtitle: null };
}

/**
 * A row's CRM tags, by name: two on a phone, three from 768px, then "+N more". The whole
 * list is in the title, and the detail shows every one. Names only — a key the vocabulary
 * cannot name reads "Unavailable tag", never the key.
 */
function RowTags({ labels }: { labels: readonly string[] }) {
  if (labels.length === 0) return null;
  const all = labels.join(', ');
  const phoneMore = labels.length - 2;
  const wideMore = labels.length - 3;
  return (
    <span className="contents" title={all} data-row-tags="">
      <span className="sr-only">CRM tags: {all}</span>
      {labels.slice(0, 3).map((t, i) => (
        <span
          key={`${i}-${t}`} aria-hidden="true" title={t}
          className={`min-w-0 max-w-[12rem] truncate rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 ${i === 2 ? 'hidden md:inline-block' : 'inline-block'}`}
          data-row-tag=""
        >
          {t}
        </span>
      ))}
      {phoneMore > 0 ? (
        <span aria-hidden="true" title={all} className="whitespace-nowrap text-xs text-slate-500 md:hidden">+{phoneMore} more</span>
      ) : null}
      {wideMore > 0 ? (
        <span aria-hidden="true" title={all} className="hidden whitespace-nowrap text-xs text-slate-500 md:inline">+{wideMore} more</span>
      ) : null}
    </span>
  );
}

/**
 * One scannable row: who (bold), their company, where from, their CRM tags, where it is
 * and when. Long values truncate and keep the whole value in the title.
 */
function Row({ row, vocab, selected, onOpen, buttonRef }: {
  row: ReviewRow;
  /** For each tag's CRM tag name; a bare key is never shown. */
  vocab: CrmTagVocabulary | undefined;
  selected: boolean;
  onOpen: () => void;
  buttonRef: (el: HTMLButtonElement | null) => void;
}) {
  const name = identityOf(row);
  const company = companyOf(row);
  // Every selected tag, stage first. An older server sent only the stage.
  const tags = row.kind === 'batch' ? [] : rowTagLabels(
    row.tags ?? (row.commercialStage ? [{ kind: 'stage', key: row.commercialStage }] : []), vocab,
  );
  const status = row.kind === 'batch' ? batchLine(row.total, row.counts) : rowStatus(row);
  const reason = rowReason(row);
  return (
    <li className="min-w-0">
      <button
        type="button"
        ref={buttonRef}
        onClick={onOpen}
        aria-current={selected ? 'true' : undefined}
        data-review-row={row.kind}
        data-review-id={row.id}
        className={`grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 rounded-lg border bg-white px-4 py-3 text-left transition md:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1.4fr)_auto] md:items-center ${FOCUS} ${
          selected ? 'border-slate-900 shadow-sm' : 'border-slate-200 hover:border-slate-400'
        }`}
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-slate-900" title={name}>{name}</span>
          {company ? <span className="block truncate text-xs text-slate-600" title={company}>{company}</span> : null}
        </span>
        <time dateTime={row.occurredAt} className="col-start-2 row-start-1 self-start whitespace-nowrap text-xs text-slate-500 md:col-start-4 md:self-center">
          {formatWhen(row.occurredAt)}
        </time>
        <span className="col-span-2 flex min-w-0 flex-wrap items-center gap-1.5 md:col-span-1 md:col-start-2 md:row-start-1">
          <span className="shrink-0 rounded border border-slate-200 px-1.5 py-0.5 text-xs font-medium text-slate-600">
            {SOURCE_LABEL[row.source]}
            {row.kind === 'email' && (row.messageCount ?? 1) > 1 ? ` · ${row.messageCount} messages` : null}
          </span>
          <RowTags labels={tags} />
        </span>
        <span className="col-span-2 min-w-0 text-xs md:col-span-1 md:col-start-3 md:row-start-1" data-review-status="">
          <span className={`inline-flex min-w-0 items-center gap-1.5 font-medium ${STATE_TONE[row.state]}`}>
            <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${STATE_DOT[row.state]}`} />
            <span className="min-w-0 [overflow-wrap:anywhere]">{status}</span>
          </span>
          {reason ? <span className="block min-w-0 text-slate-600 [overflow-wrap:anywhere]">{reason}</span> : null}
        </span>
      </button>
    </li>
  );
}

/**
 * The retired classifier's messages, kept reachable and kept out of the way.
 *
 * Shown only under All, only when there is something archived, and collapsed. Its queue's
 * pages are real requests, so they are fetched only once somebody opens it.
 */
function ArchivedDisclosure() {
  const hasHistory = useHasHistory();
  const [open, setOpen] = useState(false);
  if (!hasHistory) return null;
  return (
    <details
      className="rounded-lg border border-slate-200 bg-white/60"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      data-history=""
    >
      <summary className={`flex min-h-11 cursor-pointer items-center px-4 text-sm font-medium text-slate-600 ${FOCUS}`}>
        Archived messages
      </summary>
      {open ? (
        <div className="space-y-6 border-t border-slate-200 p-4">
          <p className="text-sm text-slate-600">
            Messages ContactPilot read before email capture, and the decisions made on them.
            Kept for reference until they age out; new mail is not added here.
          </p>
          <ArchivedMessages />
        </div>
      ) : null}
    </details>
  );
}

export function Review({ filters, onFiltersChange, onOpenCaptureSettings, onOpenCrm }: {
  filters: ReviewFilters;
  onFiltersChange: (next: ReviewFilters) => void;
  onOpenCaptureSettings?: () => void;
  onOpenCrm: () => void;
}) {
  /*
   * Polled on the same terms as the badge. Cards are read, imports resolve and captures
   * send while nobody is looking, and a list that only changes on reload stops being
   * believed.
   */
  const { data, isLoading, isFetching, error } = useReviewItemsQuery(
    { view: filters.view, source: filters.source },
    { pollingInterval: 15_000 },
  );
  /** The row as it was when opened: the drawer's title must not change under the person. */
  const [selected, setSelected] = useState<ReviewRow | null>(null);
  const vocab = useCrmTagsQuery().data;
  const rowRefs = useRef(new Map<string, HTMLButtonElement | null>());
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const sourceId = useId();
  /** What the last decision did, said once above the list. */
  const [decided, setDecided] = useState<string | null>(null);
  /** After a decision: the rows to move focus to, nearest first, once the list refreshes. */
  const focusNext = useRef<string[] | null>(null);

  // A different filter is a different list; the open item belongs to the old one.
  useEffect(() => { setSelected(null); }, [filters.view, filters.source]);

  const close = useCallback(() => {
    const id = selected?.id;
    setSelected(null);
    if (id) requestAnimationFrame(() => rowRefs.current.get(id)?.focus());
  }, [selected]);

  const viewCounts = data?.viewCounts;
  const items = data?.items ?? [];

  /*
   * A decision succeeded: close, say what happened, and move on. The list and its counts
   * refresh on their own (every decision invalidates Review); focus goes to the row after
   * the one decided — or before it, at the end — or to the heading when none is left.
   */
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const done = useCallback((message: string) => {
    const id = selected?.id;
    const list = itemsRef.current.map((r) => r.id);
    const at = id ? list.indexOf(id) : -1;
    const order = at < 0 ? [] : [...list.slice(at + 1), ...list.slice(0, at).reverse()];
    focusNext.current = order;
    setSelected(null);
    setDecided(message);
    requestAnimationFrame(() => focusFirst(order));
  }, [selected]);
  const focusFirst = (order: readonly string[]) => {
    const next = order.map((rid) => rowRefs.current.get(rid)).find((el) => el && el.isConnected);
    (next ?? headingRef.current)?.focus();
  };
  // When the refreshed list no longer holds the row that took focus, the next one does.
  useEffect(() => {
    const order = focusNext.current;
    if (!order || isFetching) return;
    const active = document.activeElement;
    if (!active || active === document.body || !active.isConnected) focusFirst(order);
    if (data && !selected) {
      const t = setTimeout(() => { focusNext.current = null; }, 4000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isFetching, data, selected]);
  // The note fades once it has been read; it is also in a live region for a screen reader.
  useEffect(() => {
    if (!decided) return undefined;
    const t = setTimeout(() => setDecided(null), 8000);
    return () => clearTimeout(t);
  }, [decided]);
  // Only once the answer is for THIS filter: a stale list under a new chip is a lie.
  const current = data && data.view === filters.view && data.source === filters.source;
  const totalForSource = viewCounts?.all ?? 0;
  const emptySourceEmail = filters.source === 'email' && totalForSource === 0;

  let detail: ReactNode = null;
  if (selected) {
    const { title, subtitle } = drawerTitle(selected);
    detail = (
      <ReviewDrawer title={title} subtitle={subtitle} onClose={close} onDone={done} wide={selected.kind === 'batch'}>
        {selected.kind === 'email'
          ? ((selected.messageCount ?? 1) > 1 ? <ConversationDetail id={selected.id} /> : <CaptureDetail id={selected.id} />)
          : null}
        {selected.kind === 'card'
          ? <BusinessCard key={selected.id} resumeId={selected.id} onOpenCrm={onOpenCrm} /> : null}
        {selected.kind === 'batch' ? <BatchDetail batchId={selected.id} onBack={close} showBack={false} /> : null}
      </ReviewDrawer>
    );
  }

  return (
    <>
      <div className="min-w-0 space-y-4">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-semibold tracking-tight text-slate-900 outline-none" data-review-heading="">Review</h1>
            <p className="mt-1 text-sm text-slate-600">
              Check what ContactPilot found before it reaches your CRM.
            </p>
            <DestinationNotice />
          </div>
          {/* Source is a filter over the views, not a section of its own — so it is the quieter control. */}
          <div className="flex shrink-0 items-center gap-2">
            <label htmlFor={sourceId} className="text-sm text-slate-500">Source</label>
            <select
              id={sourceId}
              value={filters.source}
              onChange={(e) => onFiltersChange({ ...filters, source: e.target.value as ReviewSourceFilter })}
              className={`min-h-11 min-w-36 rounded-md border border-slate-200 bg-white px-2 text-base text-slate-700 sm:text-sm ${FOCUS}`}
            >
              {REVIEW_SOURCES.map((s) => <option key={s} value={s}>{SOURCE_OPTIONS[s]}</option>)}
            </select>
          </div>
        </div>

        {/*
          Four views, always all four, each with its count. Buttons with aria-pressed rather
          than tabs: each chip filters one list rather than switching between panels. The
          strip scrolls on a narrow phone; the page does not.
        */}
        <div
          role="group"
          aria-label="Filter by state"
          className="-mx-4 flex min-w-0 gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0"
          data-review-states=""
        >
          {VIEWS.map((v) => {
            const on = filters.view === v;
            const n = viewCounts?.[v];
            return (
              <button
                key={v}
                type="button"
                aria-pressed={on}
                onClick={() => onFiltersChange({ ...filters, view: v })}
                className={`inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 text-sm ${FOCUS} ${
                  on
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                }`}
              >
                {VIEW_LABEL[v]}
                {n !== undefined ? (
                  <span className={`tabular-nums ${on ? 'text-white/80' : 'text-slate-500'}`}>{n}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div aria-live="polite" data-review-decided="">
          {decided ? <Alert tone="success">{decided}</Alert> : null}
        </div>

        {data && !data.crmWriteEnabled ? (
          <p className="text-xs text-slate-500">
            CRM sending is switched off for this workspace, so approving records your decision
            without sending anything.
          </p>
        ) : null}

        {/*
          The list owns the full width; an item opens in the drawer over it. `min-w-0`
          throughout: one hundred-character address must not set the page's width.
        */}
        <div className="min-w-0" aria-busy={isFetching ? 'true' : undefined}>
          {isLoading || (!current && isFetching) ? <Spinner label="Loading Review…" /> : null}
          {error ? <Alert tone="error">Review could not be loaded. Reload the page to try again.</Alert> : null}
          {current && items.length === 0 ? (
            <EmptyState title={emptyCopy(filters.view, filters.source, totalForSource)}>
              {emptySourceEmail && onOpenCaptureSettings ? (
                <button
                  type="button"
                  onClick={onOpenCaptureSettings}
                  className={`inline-flex min-h-11 items-center font-medium text-slate-900 underline ${FOCUS}`}
                >
                  Manage email capture
                </button>
              ) : null}
            </EmptyState>
          ) : null}
          {current && items.length > 0 ? (
            <ul className="min-w-0 space-y-2" data-review-list="">
              {items.map((row) => (
                <Row
                  key={`${row.kind}:${row.id}`}
                  row={row}
                  vocab={vocab}
                  selected={selected?.id === row.id}
                  onOpen={() => setSelected(row)}
                  buttonRef={(el) => { if (el) rowRefs.current.set(row.id, el); else rowRefs.current.delete(row.id); }}
                />
              ))}
            </ul>
          ) : null}
        </div>

        {filters.view === 'all' && (filters.source === 'all' || filters.source === 'email')
          ? <ArchivedDisclosure /> : null}
      </div>
      {/* Outside the spaced column: a margin on a fixed overlay would push it off the top. */}
      {detail}
    </>
  );
}

/**
 * Where an approval goes besides the CRM, said before anyone approves.
 *
 * A Zapier or webhook destination receives each approved contact with that destination's
 * own organisation fields; the person approving must know that, so it is stated here
 * rather than discovered later in Settings.
 */
function DestinationNotice() {
  const { data } = useDestinationsQuery();
  const active = data?.destinations?.filter((d) => d.status === 'active') ?? [];
  if (active.length === 0) return null;
  const detail = (d: { name: string; organization: { pipeline?: string | null; stage?: string | null } }) =>
    [d.name, [d.organization.pipeline, d.organization.stage].filter(Boolean).join(' / ')].filter(Boolean).join(' — ');
  return (
    <p className="mt-1 text-sm text-slate-600" data-destination-notice="">
      Approved contacts are also sent to {active.map(detail).join('; ')}.
    </p>
  );
}
